import { useIntl } from 'react-intl';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import InfoTip from '../../components/novarium/InfoTip';
import { ScheduleHoursDTO } from '../../dto/ServiceStatisticsDTO';
import ScheduleHoursChart from './ScheduleHoursChart';
import { modeMessageId, StatisticsMode } from './statisticsMode';

/** Cómo va la consulta del horario programado contra el real de la agenda elegida. */
export type ScheduleHoursStatus = 'none' | 'loading' | 'failed' | 'ready';

interface ScheduleHoursSectionProps {
    /** `none`: no hay una agenda de consultorio elegida. */
    status: ScheduleHoursStatus;
    data: ScheduleHoursDTO | null;
    onRetry: () => void;
    mode?: StatisticsMode;
}

/**
 * Bloque de la pestaña «Agendas», entre los indicadores de ocupación y el ranking: el horario programado de la agenda elegida contra el real (primer paciente
 * atendido a última atención terminada) y cuánto se pasa del cierre. Se consulta aparte del resto: si carga, falla o
 * viene vacío, solo este bloque lo dice y la pantalla sigue. Sin una agenda elegida no se muestra. El ⓘ del título dice
 * cómo se calcula lo real.
 */
export default function ScheduleHoursSection({ status, data, onRetry, mode = 'management' }: ScheduleHoursSectionProps) {
    const intl = useIntl();

    if (status === 'none') return null;

    const ready = status === 'ready' && data !== null;
    const hasHours = ready && data.scheduled && data.weekdays.length > 0;

    return (
        <section className="nv-section" aria-labelledby="ss-sched-title">
            <div className="ss-title-tip">
                <h2 className="nv-section-title" id="ss-sched-title">
                    {intl.formatMessage({ id: 'ServicesStatisticsScheduleTitle' })}
                </h2>
                <InfoTip text={intl.formatMessage({ id: modeMessageId(mode, 'ScheduleHint') })} />
            </div>
            {status === 'loading' && (
                <div className="nv-loading ss-sched__loading" role="status">
                    <ProgressSpinner strokeWidth="4" style={{ width: 24, height: 24 }} />
                    <span>{intl.formatMessage({ id: 'ServicesStatisticsLoading' })}</span>
                </div>
            )}
            {status === 'failed' && (
                <div className="ss-empty" role="status">
                    <p className="nv-muted">{intl.formatMessage({ id: 'ServicesStatisticsScheduleFailed' })}</p>
                    <Button type="button" link label={intl.formatMessage({ id: 'ServicesStatisticsRetry' })} onClick={onRetry} />
                </div>
            )}
            {ready && !data.scheduled && (
                <p className="nv-muted ss-empty">{intl.formatMessage({ id: modeMessageId(mode, 'ScheduleNotScheduled') })}</p>
            )}
            {ready && data.scheduled && !hasHours && (
                <p className="nv-muted ss-empty">{intl.formatMessage({ id: 'ServicesStatisticsScheduleNoDays' })}</p>
            )}
            {hasHours && <ScheduleHoursChart data={data} mode={mode} />}
        </section>
    );
}
